// ---------------------------------------------------------------------------
// useShellNav — hook returning the live nav for a shell
// v0.17.0 — backend-persisted platform menu config (was localStorage in v0.16).
//
// Reads the platform menu config from the API. While the request is in flight
// or if it errors, falls back to the static {admin,ops,client}MainNav defaults
// from the code, so the sidebar always renders something. When react-query
// invalidates (e.g. after a save in the WebMap editor) the hook re-resolves
// and the sidebar re-renders.
// ---------------------------------------------------------------------------

import {
  adminMainNav,
  adminBottomNav,
  commandNavStatus,
  filterNavByStatus,
  type NavItem,
} from "./adminNav";
import {
  opsMainNav,
  opsBottomNav,
  frontierNavFeatureFlag,
  frontierNavStatus,
} from "./opsNav";
import { clientMainNav, clientBottomNav, clientNavStatus } from "./clientNav";
import { useMenuConfig } from "./menuConfigApi";
import { hydrateNavList, type ShellKey } from "./navOverride";

interface ShellNav {
  main: NavItem[];
  bottom: NavItem[];
}

function getDefaultNav(shell: string): ShellNav {
  switch (shell) {
    case "admin":
      return { main: adminMainNav, bottom: adminBottomNav };
    case "ops":
      return { main: opsMainNav, bottom: opsBottomNav };
    case "client":
      return { main: clientMainNav, bottom: clientBottomNav };
    default:
      return { main: adminMainNav, bottom: adminBottomNav };
  }
}

function isAdminCatalogNavItem(item: NavItem): boolean {
  return (
    item.href === "/admin/catalog" ||
    item.href.startsWith("/admin/catalog/") ||
    item.matchPrefix === "/admin/catalog"
  );
}

function isSupportOnlyAdminTelemetryNavItem(item: NavItem): boolean {
  return (
    item.name === "Telemetry" ||
    item.name === "Dictionaries" ||
    item.name === "Dictionary Bindings" ||
    item.href === "/admin/definitions" ||
    item.href.startsWith("/admin/definitions/") ||
    item.matchPrefix === "/admin/definitions"
  );
}

function isFactoryAuthorityAdminProductionNavItem(item: NavItem): boolean {
  return (
    item.name === "Production" ||
    item.name === "Recipes" ||
    item.name === "Production Runs" ||
    item.href === "/admin/production" ||
    item.href.startsWith("/admin/production/") ||
    item.matchPrefix === "/admin/production"
  );
}

function isFactoryAuthorityAdminCapabilitiesNavItem(item: NavItem): boolean {
  return (
    item.name === "Capabilities" ||
    item.name === "Capability" ||
    item.name === "Functional Capabilities" ||
    item.href === "/admin/capabilities" ||
    item.href.startsWith("/admin/capabilities/") ||
    item.matchPrefix === "/admin/capabilities"
  );
}

function isCompatibilityAdminOperationalContractsNavItem(
  item: NavItem,
): boolean {
  return (
    item.name === "Operational Contracts" ||
    item.name === "Operational Contract" ||
    item.name === "Contracts" ||
    item.href === "/admin/operational-contracts" ||
    item.href.startsWith("/admin/operational-contracts/") ||
    item.matchPrefix === "/admin/operational-contracts"
  );
}

export function normalizeAdminCatalogNav(items: NavItem[]): NavItem[] {
  const legacyCatalogRootNames = new Set([
    "Catalog",
    "Devices",
    "Product Catalog",
    "Sentinels Products",
    "Platforms",
    "Hardware Platforms",
    "Platform Models",
    "Hardware Variants",
    "Variants",
    "Product Models",
    "Catalog Intake",
    "Factory Sync Inbox",
    "Factory Packages",
    "Source Packages",
    "Snapshots",
    "Attributes",
    "Atributos",
    "Manufacturers",
    "Fabricantes",
    "New Manufacturer",
    "New Hardware Platform",
    "Product Families",
    "Markets",
    "Taxonomies",
    "Dynamic Attributes",
    "Sheet Templates",
  ]);
  const normalized: NavItem[] = [];
  let catalogInserted = false;

  for (const item of items) {
    if (
      isSupportOnlyAdminTelemetryNavItem(item) ||
      isFactoryAuthorityAdminProductionNavItem(item) ||
      isFactoryAuthorityAdminCapabilitiesNavItem(item) ||
      isCompatibilityAdminOperationalContractsNavItem(item)
    ) {
      continue;
    }

    const normalizedChildren = item.children
      ? normalizeAdminCatalogNav(item.children)
      : undefined;
    const normalizedItem =
      item.children && normalizedChildren
        ? {
            ...item,
            children:
              normalizedChildren.length > 0 ? normalizedChildren : undefined,
          }
        : item;
    const isCatalogRoot =
      legacyCatalogRootNames.has(normalizedItem.name) ||
      isAdminCatalogNavItem(normalizedItem);
    if (!isCatalogRoot) {
      normalized.push(normalizedItem);
      continue;
    }

    if (catalogInserted) {
      continue;
    }

    normalized.push({
      ...normalizedItem,
      name: "Catalog",
      labelKey: "nav.catalog",
      href: "/admin/catalog",
      matchPrefix: "/admin/catalog",
      requiredRole: ["platform_admin"],
      requiredPermission: undefined,
      children: undefined,
    });
    catalogInserted = true;
  }

  return normalized;
}

function isAdminGlobalAuditNavItem(item: NavItem): boolean {
  return item.href === "/admin/audit";
}

/**
 * Force the global audit item to platform-admin visibility.
 *
 * The persisted menu config carries its own `requiredRole` array, so a config
 * saved before #375 keeps handing org_admin/distributor_admin a link to
 * `/api/v1/audit/global/` — an `IsHumanPlatformAdmin` surface that answers 403.
 * Re-asserting the role here means the fix holds for stored payloads too,
 * without an operator having to re-save the menu.
 */
export function normalizeAdminAuditNav(items: NavItem[]): NavItem[] {
  return items.map((item) => {
    const children = item.children
      ? normalizeAdminAuditNav(item.children)
      : undefined;
    const normalized = item.children ? { ...item, children } : item;
    if (!isAdminGlobalAuditNavItem(normalized)) {
      return normalized;
    }
    return { ...normalized, requiredRole: ["platform_admin"] };
  });
}

/**
 * Re-assert the canonical Command status on a nav tree (#379).
 *
 * The persisted menu config predates `NavItem.status`, so a stored payload
 * says nothing about what is legacy — and a payload an operator saves later
 * could claim anything. Deriving the status from the canonical table by href
 * means the curated menu holds for stored payloads too: an override can
 * reorder or rename Command entries, but it cannot put `/admin/dashboard` or
 * the inventory pages back in front of an operator.
 */
export function normalizeAdminNavStatus(items: NavItem[]): NavItem[] {
  return items.map((item) => {
    const children = item.children
      ? normalizeAdminNavStatus(item.children)
      : undefined;
    const status = commandNavStatus(item.href);
    return { ...item, ...(children ? { children } : {}), status };
  });
}

/**
 * Re-assert the canonical Frontier status on a nav tree (#383).
 *
 * Same guarantee `normalizeAdminNavStatus` gives Command: a menu-config payload
 * stored before the field existed carries no status, so hydrating it would
 * silently re-offer Ajustes and every project stage the curated menu hides. The
 * status is therefore derived from the canonical Frontier table by href, never
 * trusted from storage.
 */
export function normalizeFrontierNavStatus(items: NavItem[]): NavItem[] {
  return items.map((item) => {
    const children = item.children
      ? normalizeFrontierNavStatus(item.children)
      : undefined;
    const status = frontierNavStatus(item.href);
    // Like the status, the flag comes from the canonical table, never storage.
    const requiredFeatureFlag = frontierNavFeatureFlag(item.href);
    return {
      ...item,
      ...(children ? { children } : {}),
      status,
      ...(requiredFeatureFlag ? { requiredFeatureFlag } : {}),
    };
  });
}

/** Keep the governed Release entry present when an older persisted menu is hydrated. */
export function ensureOpsReleaseNav(items: NavItem[]): NavItem[] {
  if (items.some((item) => item.href === "/ops/release")) return items;
  const release = opsMainNav.find((item) => item.href === "/ops/release");
  return release ? [...items, release] : items;
}

/**
 * Proposals arrived after menus could be saved (D-AE5 = B): a stored Kodiak
 * payload without it still offers it, right after Projects, as
 * `ensureOpsGarageNav` does for Frontier.
 */
export function ensureClientProposalsNav(items: NavItem[]): NavItem[] {
  if (items.some((item) => item.href === "/client/proposals")) return items;
  const proposals = clientMainNav.find(
    (item) => item.href === "/client/proposals",
  );
  if (!proposals) return items;
  const projects = items.findIndex((item) => item.href === "/client/projects");
  if (projects < 0) return [...items, proposals];
  return [
    ...items.slice(0, projects + 1),
    proposals,
    ...items.slice(projects + 1),
  ];
}

/**
 * Garage arrived after menus could be saved: a stored Ops payload without it
 * still offers it, right after Proyectos, as `ensureOpsReleaseNav` does.
 */
export function ensureOpsGarageNav(items: NavItem[]): NavItem[] {
  if (items.some((item) => item.href === "/ops/garage")) return items;
  const garage = opsMainNav.find((item) => item.href === "/ops/garage");
  if (!garage) return items;
  const projects = items.findIndex((item) => item.href === "/ops/projects");
  if (projects < 0) return [...items, garage];
  return [
    ...items.slice(0, projects + 1),
    garage,
    ...items.slice(projects + 1),
  ];
}

/**
 * The project's own menu (its stages and tools) belongs to the code, not to a
 * persisted menu: a stored payload keeps its top level, and Proyectos always
 * carries the canonical project entries, in their order and with their groups.
 */
export function ensureOpsProjectTabs(items: NavItem[]): NavItem[] {
  const projects = opsMainNav.find((item) => item.href === "/ops/projects");
  if (!projects) return items;
  return items.map((item) =>
    item.href === "/ops/projects"
      ? { ...item, children: projects.children }
      : item,
  );
}

/**
 * Re-assert the canonical Kodiak status on a nav tree (#394).
 *
 * Same guarantee `normalizeAdminNavStatus` and `normalizeFrontierNavStatus`
 * give Command and Frontier: a persisted `shells.client` payload stored before
 * the field existed carries no status, and one saved later could claim
 * anything, so hydrating it would re-offer Dashboards, Umbrales and
 * Organización to a client. The status is derived from the canonical Kodiak
 * table by href, never trusted from storage.
 */
export function normalizeClientNavStatus(items: NavItem[]): NavItem[] {
  return items.map((item) => {
    const children = item.children
      ? normalizeClientNavStatus(item.children)
      : undefined;
    const status = clientNavStatus(item.href);
    return { ...item, ...(children ? { children } : {}), status };
  });
}

/**
 * Returns the live nav for the given shell. Reactive to backend-persisted
 * menu config changes via react-query invalidation. Falls back to the
 * static code defaults when no override exists or the request hasn't
 * resolved yet.
 *
 * The status filter runs last, after the override has been merged and
 * normalized, so no persisted payload can re-offer a legacy surface.
 */
export function useShellNav(shell: string): ShellNav {
  const { data } = useMenuConfig();
  const isKnownShell =
    shell === "admin" || shell === "ops" || shell === "client";
  const override = isKnownShell
    ? data?.payload?.shells?.[shell as ShellKey]
    : undefined;

  if (override) {
    const main = hydrateNavList(override.main);
    const bottom = hydrateNavList(override.bottom);
    const normalize = (items: NavItem[], isMain: boolean): NavItem[] => {
      if (shell === "admin") {
        return normalizeAdminNavStatus(
          normalizeAdminAuditNav(
            isMain ? normalizeAdminCatalogNav(items) : items,
          ),
        );
      }
      if (shell === "ops") {
        return normalizeFrontierNavStatus(
          isMain
            ? ensureOpsProjectTabs(
                ensureOpsGarageNav(ensureOpsReleaseNav(items)),
              )
            : items,
        );
      }
      if (shell === "client") {
        return normalizeClientNavStatus(
          isMain ? ensureClientProposalsNav(items) : items,
        );
      }
      return items;
    };
    return {
      main: filterNavByStatus(normalize(main, true)),
      bottom: filterNavByStatus(normalize(bottom, false)),
    };
  }
  const defaults = getDefaultNav(shell);
  return {
    main: filterNavByStatus(defaults.main),
    bottom: filterNavByStatus(defaults.bottom),
  };
}
